import React, { useState, useEffect, Fragment } from 'react';
import { Card, List, Tag, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { LoadingOutlined, ExclamationCircleOutlined, StopOutlined } from '@ant-design/icons';
import InventoryService from '../inventory-items/services';

import CardEmptyResult from 'components/common/pageComponents/CardEmptyResult';
import { PiPackageDuotone } from 'react-icons/pi';

const InventoryAlertsWidget = () => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchInventoryAlerts();
  }, []);

  const fetchInventoryAlerts = async () => {
    try {
      const [lowStockResponse, outOfStockResponse] = await Promise.all([
        InventoryService.getLowStock(),
        InventoryService.getOutOfStock()
      ]);

      const lowRows = Array.isArray(lowStockResponse?.data?.data) ? lowStockResponse.data.data : [];
      const outRows = Array.isArray(outOfStockResponse?.data?.data) ? outOfStockResponse.data.data : [];

      const alerts = [
        ...outRows.map(row => ({
          ...row,
          type: 'out_of_stock',
          rowKey: `out:${row.id}:${row.warehouse_id || 'unassigned'}`,
          message: row.warehouse_name
            ? `Out of stock in ${row.warehouse_name}`
            : 'Out of stock',
        })),
        ...lowRows.map(row => ({
          ...row,
          type: 'low_stock',
          rowKey: `low:${row.id}:${row.warehouse_id || 'unassigned'}`,
          message: row.warehouse_name
            ? `${row.current_stock} in ${row.warehouse_name} (reorder at ${row.reorder_point})`
            : `Low stock: ${row.current_stock} remaining (reorder at ${row.reorder_point})`,
        })),
      ];

      setAlerts(alerts.slice(0, 8)); // Limit to 8 items
    } catch (error) {
      console.error('Error fetching inventory alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type) => {
    return type === 'out_of_stock' ?
      <StopOutlined style={{ color: '#ff4d4f' }} /> :
      <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
  };

  const getAlertTag = (type, item) => {
    if (type === 'out_of_stock') {
      return <Tag color="red">Out of Stock</Tag>;
    }
    return <Tag color="orange">Low Stock ({item.current_stock})</Tag>;
  };

  if (loading) {
    return <Spin indicator={<LoadingOutlined spin />} />;
  }

  return (
    <Fragment>
      {alerts.length > 0 ? (
        <Card 
          size="small"
          title="Inventory Alerts"
          extra={<Link to="/inventory-items/list?stock_status=low_stock">View All</Link>}
          styles={{body: {padding: '0px', paddingTop: '10px'}}}
        >
          <List
            size="small"
            dataSource={alerts}
            renderItem={(item) => (
              <List.Item key={item.rowKey}>
                <List.Item.Meta
                  avatar={getAlertIcon(item.type)}
                  title={
                    <Link to={`/inventory-items/item-details/${item.id}`}>
                      {item.name} ({item.sku})
                    </Link>
                  }
                  description={item.message}
                />
                <div>
                  {getAlertTag(item.type, item)}
                </div>
              </List.Item>
            )}
          />
        </Card>
      ) : (
        <CardEmptyResult title="No inventory alerts" subtitle="All items are well stocked" icon={<PiPackageDuotone className="fs-2 text-secondary" />} />
      )}
    </Fragment>
  );
};

export default InventoryAlertsWidget;
